import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "booking-room-selector" }
const _hoisted_2 = {
  for: "roomSelector",
  class: "form-label"
}
const _hoisted_3 = {
  key: 0,
  class: "text-danger"
}
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = {
  key: 1,
  class: "alert alert-warning mb-0"
}

import { computed, ref, nextTick } from "vue";
import VueMultiselect from "vue-multiselect";
import { useMasterDataHelpers } from "../composables/useMasterDataHelpers";
import { generateResourceIconClass } from "../utils/resourceIconUtils";


export default {
  __name: 'BookingRoomSelector',
  props: {
	form: { type: Object, required: true },
	disabled: { type: Boolean, default: false },
	currentRoomId: { type: Number, default: null }, // For edit mode: always include this room even without permission
	allowEmpty: { type: Boolean, default: false }, // Allow clearing the room (planning stage, e.g. course appointments)
},
  setup(__props) {

const props = __props;

const masterData = useMasterDataHelpers();

// Extract form for template
const { form } = props;

// Ref for the multiselect component
const multiselectRef = ref(null);

// Transform roomsByBuilding data into vue-multiselect group format
// Only includes rooms where the user has canCreateRegular permission
// Exception: in edit mode, always include the current room (via currentRoomId prop)
const buildingsWithRooms = computed(() => {
	const buildings = masterData.roomsByBuilding.value;

	// Convert object to array and transform for vue-multiselect groups
	// Buildings are kept in server order (same as Dashboard and Calendar)
	return Object.values(buildings)
		.filter(
			(building) =>
				building &&
				building.id &&
				building.name &&
				building.rooms &&
				building.rooms.length > 0
		)
		.map((building) => ({
			id: building.id,
			name: building.name,
			short: building.short,
			groupLabel: building.short
				? `${building.short}: ${building.name}`
				: building.name,
			rooms: building.rooms
				.filter(
					(room) =>
						// Include room if user has permission to create bookings in it
						room.permissions?.canCreateRegular ||
						// Or if this is the currently selected room (for edit mode)
						room.id === props.currentRoomId
				)
				.sort((a, b) => {
					const aLabel = a.doorLabel || a.name || "";
					const bLabel = b.doorLabel || b.name || "";
					return aLabel.localeCompare(bLabel, "de", { numeric: true });
				})
				.map((room) => ({
					...room,
					buildingShort: building.short,
				})),
		}))
		// Filter out buildings that have no rooms after permission filtering
		.filter((building) => building.rooms.length > 0);
});

// Check if user has any rooms available
const hasAvailableRooms = computed(() => {
	return buildingsWithRooms.value.some((building) => building.rooms.length > 0);
});

// Get room object from roomId for v-model binding
function getSelectedRoom(roomId) {
	if (!roomId) return null;

	// Find the room in the buildings
	for (const building of buildingsWithRooms.value) {
		const foundRoom = building.rooms.find((r) => r.id === roomId);
		if (foundRoom) {
			return foundRoom;
		}
	}

	return null;
}

// Get display label for room (matches RoomNavigator pattern)
function getRoomDisplayLabel(room) {
	if (!room) return "";
	return room.doorLabel + (room.name ? " " + room.name : "");
}

// Get full room name for closed dropdown (buildingShort + doorLabel)
function getFullRoomName(room) {
	if (!room) return "";
  let label = "";
  if (room.buildingShort) label += room.buildingShort + ' ';
  label += room.doorLabel;
  if (room.name) label += ' ' + room.name;
	return label;
}

// Handle room selection change
function handleRoomChange(room, field) {
	if (room && room.id) {
		field.handleChange(room.id);
	} else if (props.allowEmpty) {
		// Room was cleared (deselected) and empty selection is permitted
		field.handleChange(null);
	}
}

// Scroll to selected room when dropdown opens
function scrollToSelectedRoom(roomId) {
	if (!roomId || !multiselectRef.value) return;

	nextTick(() => {
		const listElement = multiselectRef.value.$refs.list;
		if (!listElement) return;

		// Flatten the grouped options to match VueMultiselect's internal list
		// Format: [groupLabel1, room1, room2, ..., groupLabel2, room1, ...]
		let flatIndex = -1;
		let found = false;

		for (const building of buildingsWithRooms.value) {
			// Count the group label
			flatIndex++;

			// Check each room in this building
			for (const room of building.rooms) {
				flatIndex++;
				if (room.id === roomId) {
					found = true;
					break;
				}
			}

			if (found) break;
		}

		if (found) {
			// VueMultiselect uses 40px as default optionHeight
			const optionHeight = 32;
			const scrollPosition = flatIndex * optionHeight;

			// Scroll to position, centering the selected item
			listElement.scrollTop = Math.max(
				0,
				scrollPosition - listElement.clientHeight / 2
			);
		}
	});
}

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(form).Field, { name: "roomId" }, {
      default: _withCtx(({ field }) => [
        _createElementVNode("label", _hoisted_2, [
          _cache[0] || (_cache[0] = _createTextVNode(" Raum ", -1 /* CACHED */)),
          (!__props.allowEmpty)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
            : _createCommentVNode("v-if", true)
        ]),
        (hasAvailableRooms.value)
          ? (_openBlock(), _createBlock(_unref(VueMultiselect), {
              key: 0,
              id: "roomSelector",
              ref_key: "multiselectRef",
              ref: multiselectRef,
              "model-value": getSelectedRoom(field.state.value),
              options: buildingsWithRooms.value,
              "group-values": "rooms",
              "group-label": "groupLabel",
              "group-select": false,
              "track-by": "id",
              label: "doorLabel",
              searchable: true,
              "show-labels": false,
              "allow-empty": __props.allowEmpty,
              placeholder: "Raum auswählen",
              disabled: __props.disabled,
              "onUpdate:modelValue": (room) => handleRoomChange(room, field),
              onOpen: $event => (scrollToSelectedRoom(field.state.value))
            }, {
              singleLabel: _withCtx(({ option }) => [
                _createTextVNode(_toDisplayString(getFullRoomName(option)), 1 /* TEXT */)
              ]),
              option: _withCtx(({ option }) => [
                (option.$isLabel)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(option.$groupLabel), 1 /* TEXT */))
                  : (_openBlock(), _createElementBlock("span", _hoisted_5, [
                      _createElementVNode("i", {
                        class: _normalizeClass(_unref(generateResourceIconClass)(option))
                      }, null, 2 /* CLASS */),
                      _createTextVNode(" " + _toDisplayString(getRoomDisplayLabel(option)), 1 /* TEXT */)
                    ]))
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["model-value", "options", "allow-empty", "disabled", "onUpdate:modelValue", "onOpen"]))
          : (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[1] || (_cache[1] = [
              _createElementVNode("i", { class: "fas fa-exclamation-triangle me-2" }, null, -1 /* CACHED */),
              _createTextVNode(" Keine Räume verfügbar. Sie sind keiner Buchungsgruppe zugeordnet. ", -1 /* CACHED */)
            ]))]))
      ]),
      _: 1 /* STABLE */
    })
  ]))
}
}

}